import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Briefcase, MapPin, Clock, Users, CheckCircle } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import StaffHeader from './StaffHeader';
import { isoDate } from '../../utils/time';
import { EmptyState, ErrorState, TableSkeleton, fmtTimeRange, humanise, num } from '../../components/ui';
import { useStaffRecord, toDay } from './hrKit';

const dayLong = (v) => { const d = toDay(v); return d ? d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '—'; };

function calcHours(start, end) {
  if (!start || !end) return null;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 1440;
  return mins / 60;
}

export default function StaffAvailableShifts() {
  const { id } = useParams();
  const { staff, error: staffError, reload: reloadStaff } = useStaffRecord(id);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [claiming, setClaiming] = useState(null);
  const [filters, setFilters] = useState({ from: isoDate(new Date()), to: '' });

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.from) params.set('start_date', filters.from);
    if (filters.to) params.set('end_date', filters.to);
    const qs = params.toString();
    setLoading(true); setLoadError(null);
    api.get(`/shifts/board/vacant${qs ? `?${qs}` : ''}`)
      .then((r) => setShifts(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load the job board. Try again."))
      .finally(() => setLoading(false));
  }, [filters.from, filters.to]);
  useEffect(() => { load(); }, [load]);

  async function claimShift(shift) {
    setClaiming(shift.id);
    try {
      await api.post(`/shifts/${shift.id}/apply`, { staffId: id });
      toast.success(`Applied for ${dayLong(shift.shift_date)} on ${staff ? staff.first_name : 'their'} behalf`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "The application wasn't sent. Try again.");
    } finally {
      setClaiming(null);
    }
  }

  return (
    <div>
      <StaffHeader staff={staff} error={staffError} onRetry={reloadStaff} />
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-[13px] text-slate-600">Vacant shifts on the job board that this worker can apply for.</p>
          <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
            <label className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-slate-600">From
              <input type="date" value={filters.from} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[13px]" />
            </label>
            <label className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-slate-600">To
              <input type="date" value={filters.to} min={filters.from || undefined} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[13px]" />
            </label>
          </div>
        </div>

        {loading ? <div className="rounded-xl border border-slate-200 bg-white"><TableSkeleton rows={4} cols={3} /></div>
          : loadError ? <div className="rounded-xl border border-slate-200 bg-white"><ErrorState message={loadError} onRetry={load} /></div>
            : shifts.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-white">
                <EmptyState icon={Briefcase} title="No vacant shifts" hint="Nothing on the job board for these dates. Try a wider date range." />
              </div>
            ) : (
              <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {shifts.map((shift) => {
                  const hours = calcHours(shift.start_time, shift.end_time);
                  return (
                    <li key={shift.id} className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{dayLong(shift.shift_date)}</p>
                          <p className="text-[12.5px] text-slate-500">{shift.shift_type ? humanise(shift.shift_type) : 'Standard'}</p>
                        </div>
                        {hours != null && <span className="badge-gray tabular-nums">{num(hours, 1)} h</span>}
                      </div>
                      <ul className="space-y-1 text-[13px] text-slate-600">
                        <li className="flex items-center gap-1.5"><Clock size={13} className="shrink-0 text-slate-400" aria-hidden="true" />{fmtTimeRange(shift.start_time, shift.end_time)}</li>
                        {shift.client_names && <li className="flex items-center gap-1.5"><Users size={13} className="shrink-0 text-slate-400" aria-hidden="true" /><span className="truncate">{shift.client_names}</span></li>}
                        {(shift.location || shift.address) && <li className="flex items-center gap-1.5"><MapPin size={13} className="shrink-0 text-slate-400" aria-hidden="true" /><span className="truncate">{shift.location || shift.address}</span></li>}
                      </ul>
                      <button type="button" onClick={() => claimShift(shift)} disabled={claiming === shift.id} className="btn-primary btn-sm mt-auto justify-center">
                        <CheckCircle size={13} aria-hidden="true" />{claiming === shift.id ? 'Applying…' : 'Apply for this shift'}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
      </div>
    </div>
  );
}
